import { Button, IconButton } from '@creditornot/cb-button';
import { ArrowLeft } from '@creditornot/cb-icons';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';

import StepsBar from './StepsBar/StepsBar';
import { SelfServeWizardStep } from '../types';

export type WizardProps = {
  stepsCount: number;
  currentStepIdentifier: string;
  wizardData: SelfServeWizardStep[];
  invalidateCache: () => void;
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
`;

const TitleWrapper = styled(T.Heading5)`
  font-weight: 600;
`;

const DataComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const ButtonsWrapper = styled.div`
  display: flex;
`;

const StyledButton = styled(Button).attrs({
  size: 'large',
})`
  flex-grow: 1;
`;

const StyledArrowLeft = styled(ArrowLeft)`
  width: 20px;
`;

const BackButton = styled(IconButton).attrs({ variant: 'blue', size: 'small' })`
  border-radius: 12px;
  margin-inline-end: 12px;
  height: 56px;
  width: 56px;

  div {
    border-radius: 8px;
    height: 56px;
    width: 56px;
  }
`;

const Wizard = ({ stepsCount, currentStepIdentifier, wizardData, invalidateCache }: WizardProps) => {
  const { search } = useLocation();
  const history = useHistory();
  const { formatMessage } = useIntl();
  const [updateIsInProgress, setUpdateIsInProgress] = useState<boolean>(false);
  const [nextButtonDisabled, setNextButtonDisabled] = useState<boolean>(false);

  const hasWizardData = Boolean(wizardData.length);
  if (!hasWizardData) {
    return null;
  }

  const currentStepIndex = wizardData.findIndex(wizardEntry => wizardEntry.identifier === currentStepIdentifier);
  const currentStepData = wizardData[currentStepIndex];

  const hasPrevStep = currentStepIndex > 0;
  const hasNextStep = currentStepIndex < wizardData.length - 1;

  const goToPrevStep = () =>
    hasPrevStep
      ? history.push(
          search.replace(wizardData[currentStepIndex].identifier, wizardData[currentStepIndex - 1].identifier),
        )
      : null;

  const goToNextStep = () => {
    invalidateCache();
    return hasNextStep
      ? history.push(
          search.replace(wizardData[currentStepIndex].identifier, wizardData[currentStepIndex + 1].identifier),
        )
      : null;
  };

  return (
    <Wrapper data-testid="onboarding-wizard-component">
      <StepsBar count={stepsCount} percentageFilled={currentStepData.percentageFilled} />
      <TitleWrapper>{currentStepData.title}</TitleWrapper>

      <DataComponentWrapper>
        {React.createElement(currentStepData.component, {
          goToNextStep,
          goToPrevStep,
          setUpdateIsInProgress,
          setNextButtonDisabled,
        })}
      </DataComponentWrapper>
      <ButtonsWrapper>
        {hasPrevStep && (
          <BackButton onClick={goToPrevStep}>
            <StyledArrowLeft />
          </BackButton>
        )}
        <StyledButton
          type="submit"
          form={currentStepData.identifier}
          variant="blue"
          loading={updateIsInProgress}
          disabled={nextButtonDisabled}
        >
          {formatMessage({ id: 'self-serve-sign-up.wizard.next' })}
        </StyledButton>
      </ButtonsWrapper>
    </Wrapper>
  );
};

export default Wizard;
